import { BffApiClientMolecule } from "@/app/api-client/store"
import { BlockchainFiltersMoleculeReatom as BlockchainFiltersMolecule } from "@/app/blockchains/filters/blockchain-filters-molecule.reatom"
import {
  getBlockchainFromAddress,
  getRawAddress,
} from "@anaconda/core/blockchain/address"
import type { Blockchain } from "@anaconda/core/blockchain/types"
import { atom, computed } from "@reatom/core"
import { molecule } from "bunshi"
import { getCollectionStatisticQueryParams } from "../statistic/collection-statistic-query"
import { getCollectionActivityQueryParams } from "./collection-activity-query"
import { getCollectionFloorItemQueryParams } from "./collection-floor-item-query"
import { getCollectionQueryParams } from "./collection-query"
import { CollectionScope } from "./scope"
import { getTraitsQueryParams } from "./traits-query"

export const CollectionMoleculeReatom = molecule((mol, scope) => {
  const { collectionId } = scope(CollectionScope)
  const { blockchainsQueryFilter } = mol(BlockchainFiltersMolecule)
  const bffApiClient = mol(BffApiClientMolecule)

  // Blockchain is static - derived from collection ID once
  const blockchain = getBlockchainFromAddress(collectionId)
  const collectionAddress = getRawAddress(collectionId)

  // Create an atom to hold blockchain filter value (for reactive updates)
  // This reads from blockchainsQueryFilterAtom when needed
  const blockchainsFilterAtom = atom<Blockchain[]>([], "blockchainsFilter")

  // Collection query params - returns TanStack Query params
  const collectionQueryParamsAtom = computed(() => {
    return getCollectionQueryParams(bffApiClient, {
      id: collectionId,
    })
  }, "collectionQueryParams")

  // Statistics query params - with 5s refetch interval
  const statisticQueryParamsAtom = computed(() => {
    return {
      ...getCollectionStatisticQueryParams(bffApiClient, {
        id: collectionId,
      }),
      refetchInterval: 5000,
      retry: false,
    }
  }, "statisticQueryParams")

  // Traits query params - returns available traits for filtering
  const collectionTraitsQueryParamsAtom = computed(() => {
    return getTraitsQueryParams(bffApiClient, {
      id: collectionId,
    })
  }, "collectionTraitsQueryParams")

  // Activity query params - infinite query for activity table
  const collectionActivityQueryParamsAtom = computed(() => {
    return getCollectionActivityQueryParams(bffApiClient, { id: collectionId })
  }, "collectionActivityQueryParams")

  // Floor price item query params - depends on blockchain filter
  const nftWithFloorPriceQueryParamsAtom = computed(() => {
    const blockchains = blockchainsFilterAtom()
    return getCollectionFloorItemQueryParams(bffApiClient, {
      collectionId,
      blockchains,
    })
  }, "nftWithFloorPriceQueryParams")

  return {
    collectionQueryParamsAtom,
    collectionTraitsQueryParamsAtom,
    collectionId,
    collectionAddress,
    blockchain,
    statisticQueryParamsAtom,
    collectionActivityQueryParamsAtom,
    nftWithFloorPriceQueryParamsAtom,
    blockchainsFilterAtom,
  }
})
